import type { ButtonHTMLAttributes } from 'react'
import { cn } from '../../utils/cn'

export type IconButtonVariant = 'plain' | 'tonal' | 'outlined'
export type IconButtonSize = 'sm' | 'md' | 'lg'

const VARIANTS: Record<IconButtonVariant, string> = {
  plain: 'text-on-surface-variant hover:bg-primary/5 hover:text-primary',
  tonal: 'bg-surface-container-high text-on-surface-variant hover:bg-primary/10 hover:text-primary',
  outlined: 'border border-outline-variant text-on-surface-variant hover:border-primary hover:text-primary',
}

const SIZES: Record<IconButtonSize, { box: string; glyph: string }> = {
  sm: { box: 'h-8 w-8', glyph: 'text-[18px]' },
  md: { box: 'h-10 w-10', glyph: 'text-[22px]' },
  lg: { box: 'h-12 w-12', glyph: 'text-[24px]' },
}

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Material Symbols glyph name, e.g. "close" or "calendar_month". */
  icon: string
  /** Required: the glyph carries no accessible name of its own. */
  label: string
  variant?: IconButtonVariant
  size?: IconButtonSize
  filled?: boolean
}

export function IconButton({
  icon,
  label,
  variant = 'plain',
  size = 'md',
  filled = false,
  className,
  ...rest
}: IconButtonProps) {
  const s = SIZES[size]
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      {...rest}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full transition-colors duration-300',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        'disabled:pointer-events-none disabled:opacity-40',
        VARIANTS[variant],
        s.box,
        className,
      )}
    >
      <span className={cn('material-symbols-outlined', s.glyph, filled && 'filled')}>{icon}</span>
    </button>
  )
}
